export type Lang = 'fr' | 'en';

export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
];

export type Dict = Record<string, string>;
/** A translation fragment: the same set of keys in every supported language. */
export interface DictFragment {
  fr: Dict;
  en: Dict;
}

// Fragments are split by area so that work can be parallelised without merge
// conflicts. Each fragment owns a distinct set of key prefixes; `common.*` keys
// live only in `common`. Duplicate keys across fragments are a bug and throw at
// load time (and are caught by scripts/check-i18n.ts).
import { cde } from './dicts/cde';
import { common } from './dicts/common';
import { shell } from './dicts/shell';
import { views } from './dicts/views';
import { projects } from './dicts/projects';
import { projectParts } from './dicts/projectParts';
import { projectExtras } from './dicts/projectExtras';
import { tasks } from './dicts/tasks';
import { tasksPlanning } from './dicts/tasksPlanning';
import { admin } from './dicts/admin';
import { inventories } from './dicts/inventories';
import { projectFlow } from './dicts/projectFlow';
import { echeances } from './dicts/echeances';
import { resources } from './dicts/resources';
import { proposals } from './dicts/proposals';
import { recorder } from './dicts/recorder';

const fragments: Record<string, DictFragment> = {
  cde,
  common,
  shell,
  views,
  projects,
  projectParts,
  projectExtras,
  tasks,
  tasksPlanning,
  admin,
  inventories,
  projectFlow,
  echeances,
  resources,
  proposals,
  recorder,
};

function build(lang: Lang): Dict {
  const out: Dict = {};
  for (const [name, frag] of Object.entries(fragments)) {
    for (const [key, value] of Object.entries(frag[lang])) {
      if (key in out) {
        throw new Error(`Duplicate i18n key "${key}" (redefined in fragment "${name}")`);
      }
      out[key] = value;
    }
  }
  return out;
}

export const dictionaries: Record<Lang, Dict> = {
  fr: build('fr'),
  en: build('en'),
};
